import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { SessionService } from './session.service';

export type TipoAposentadoria =
  | 'RESERVA_REMUNERADA_A_PEDIDO'
  | 'RESERVA_REMUNERADA_EX_OFFICIO'
  | 'REFORMA_POR_INCAPACIDADE'
  | 'REFORMA_POR_IDADE'
  | 'REFORMA_POR_TEMPO_DE_CONTRIBUICAO';

export interface CriarProcessoPayload {
  matricula: string;
  idOrgao: number;
  tipoAposentadoria: TipoAposentadoria;
}

export interface VinculoResumo {
  id: number;
  matricula: string;
  cpf: string;
  nome: string;
  rg: string;
  dataNascimento: string;
  orgao: { id: number; sigla: string; descricao: string };
}

export interface ProcessoExistente {
  idBeneficio: number;
  numeroBeneficio: string;
  numeroProcesso: string;
}

export interface TipoAposentadoriaItem {
  codigo: string;
  descricao: string;
}

export interface DadosAberturaResponse {
  vinculo: VinculoResumo;
  tiposAposentadoria: TipoAposentadoriaItem[];
  podeAbrirProcesso: boolean;
  processoExistente: ProcessoExistente | null;
}

export interface TramitarPayload {
  idLocalDestino: number;
  observacao: string;
  idUsuario?: number | null;
}

export interface LocalProcesso {
  id: number;
  nome: string;
  descricao: string;
}

export interface PesquisaProcessoFiltro {
  tipo?: string;
  status?: string;
  numProc?: string;
  numBen?: string;
  matricula?: string;
  idOrgao?: number | string;
  idLocalAtual?: number | string;
  cpf?: string;
  /** Nome do servidor. O backend ainda não filtra por nome (GET /processo/pesquisa) — o parâmetro já é enviado para quando o filtro existir. */
  nome?: string;
}

@Injectable({ providedIn: 'root' })
export class ProcessoService {
  consultarTentativa(matricula:string,idOrgao:number):Observable<any> {
    return this.http.get(`${environment.URL_API}/processo/tentativa-abertura`,{params:{matricula,idOrgao}});
  }
  conciliarTentativa(matricula:string,idOrgao:number,dados:any):Observable<void> {
    return this.http.post<void>(`${environment.URL_API}/processo/tentativa-abertura`,dados,{params:{matricula,idOrgao}});
  }
  discriminativoOriginal(idBeneficio:number):Observable<Blob> {
    return this.http.get(`${environment.URL_API}/composicao-ato/beneficio/${idBeneficio}/quadro`,{responseType:'blob'});
  }

  listarOrgaos(): Observable<{label:string;value:number}[]> {
    return this.http.get<{label:string;value:number}[]>(`${environment.URL_API}/processo/orgaos`);
  }
  cancelarTramitacao(idBeneficio:number,idTramitacao:number,motivo:string) {
    return this.http.post(`${environment.URL_API}/processo/${idBeneficio}/tramitacoes/${idTramitacao}/cancelar`,{motivo});
  }
  private urlServer = environment.URL_API;

  constructor(private http: HttpClient, private session: SessionService) {}

  getDadosAbertura(matricula: string, idOrgao: number | string): Observable<DadosAberturaResponse> {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${this.session.token}`,
      'Content-Type': 'application/json',
    });

    return this.http.post<DadosAberturaResponse>(
      `${this.urlServer}/processo/dados-abertura`,
      { matricula, idOrgao },
      { headers }
    );
  }

  criarProcesso(payload: CriarProcessoPayload): Observable<any> {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${this.session.token}`,
      'Content-Type': 'application/json',
    });

    return this.http.post<any>(`${this.urlServer}/processo/criar`, payload, { headers });
  }

  listarLocais(): Observable<LocalProcesso[]> {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${this.session.token}`,
    });
    return this.http.get<LocalProcesso[]>(`${this.urlServer}/local-processo`, { headers });
  }

  tramitarProcesso(
    idProcesso: number | string,
    payload: TramitarPayload
  ): Observable<any> {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${this.session.token}`,
      'Content-Type': 'application/json',
    });

    return this.http.post<any>(
      `${this.urlServer}/processo/${idProcesso}/tramitar`,
      payload,
      { headers }
    );
  }

  getProcessoCompleto(idBeneficio: number | string): Observable<any> {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${this.session.token}`,
    });

    return this.http.get<any>(
      `${this.urlServer}/processo/${idBeneficio}/completo`,
      { headers }
    );
  }

  pesquisarProcessos(
    page: number,
    maxpage: number,
    filtro: PesquisaProcessoFiltro
  ): Observable<any> {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${this.session.token}`,
    });

    let params = new HttpParams().set('page', page).set('maxpage', maxpage);
    Object.entries(filtro).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        params = params.set(key, String(value));
      }
    });

    return this.http.get<any>(`${this.urlServer}/processo/pesquisa`, { headers, params });
  }
}
